import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableRow,
  Table,
  TableBody,
  TableCell,
  Checkbox,
  Chip,
  Stack,
  Grid2 as Grid,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import {
  MailListSummary,
  MailList,
} from "api/memberPortal/mailList/mailList.types";
import { niapColorFamily } from "utils/color";
import { pluralize } from "utils/stringFormatting";

interface MailListMembersListProps {
  mailLists: MailListSummary[] | MailList[];
  collapsed?: boolean;
  enableSelection?: boolean;
  selectedIds?: number[];
  onSelectionChange?: (_ids: number[]) => void;
}

export default function MailListMembersList({
  mailLists,
  collapsed = true,
  enableSelection = false,
  selectedIds = [],
  onSelectionChange,
}: MailListMembersListProps) {
  const navigate = useNavigate();

  const handleToggle = (id: number) => {
    if (!onSelectionChange) return;
    const next = selectedIds.includes(id)
      ? selectedIds.filter((selectedId) => selectedId !== id)
      : [...selectedIds, id];
    onSelectionChange(next);
  };

  if (!mailLists) return <></>;

  const cardByCategory = Object.groupBy(mailLists, (card) => {
    if (card.category) return card.category?.name;
    else return "General";
  });

  const categories = Object.keys(cardByCategory).sort((a, b) =>
    a.localeCompare(b),
  );

  return (
    <Grid container sx={{ flex: 1, flexDirection: "column" }}>
      {categories.map((categoryName) => {
        const lists = cardByCategory[categoryName];
        if (!lists) return <></>;

        const categoryColor = lists[0]?.category?.themeColor ?? "ink.light";

        return (
          <Grid key={categoryName}>
            <Accordion
              sx={{
                boxShadow: 0,
                border: "none",
                "&::before": { display: "none" },
              }}
              defaultExpanded={!collapsed}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{
                  borderLeft: 10,
                  borderColor: categoryColor,
                  minHeight: 36,

                  "&.Mui-expanded": {
                    minHeight: 36,
                  },
                  "& .MuiAccordionSummary-content": {
                    margin: "4px 0",
                    "&.Mui-expanded": {
                      margin: "4px 0",
                    },
                  },
                  "&:hover": {
                    backgroundColor: "glyph.main",
                  },
                }}
              >
                <Stack
                  direction="row"
                  spacing={2}
                  sx={{
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="overline">{categoryName}</Typography>
                  <Chip
                    label={`${pluralize(lists.length, "List")}`}
                    size="small"
                    color={niapColorFamily(categoryColor)}
                  />
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                <Table size="small">
                  <TableBody
                    sx={{
                      "& tr:nth-of-type(odd)": {
                        backgroundColor: "white",
                      },
                      "& tr:hover": {
                        backgroundColor: "glyph.main",
                      },
                      borderLeft: 10,
                      borderColor: categoryColor,
                    }}
                  >
                    {lists.map((list) => {
                      const isSelected = selectedIds.includes(list.id);
                      return (
                        <TableRow
                          key={list.id}
                          onClick={() =>
                            enableSelection
                              ? handleToggle(list.id)
                              : navigate(
                                  `/community/mailing-lists/details/${list.id}`,
                                )
                          }
                          sx={{
                            cursor: "pointer",
                            backgroundColor: isSelected
                              ? `${categoryColor}14`
                              : "white",
                          }}
                        >
                          {enableSelection && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={isSelected}
                                size="small"
                                tabIndex={-1}
                              />
                            </TableCell>
                          )}
                          <TableCell>
                            <Typography variant="caption">
                              {list.name}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="caption">
                              {list.email}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          </Grid>
        );
      })}
    </Grid>
  );
}
